import { Button, Heading } from '@/components/atoms';
import {
  AnalyticsChart,
  MetricCard,
  DataTable,
  AnalyticsEmptyState,
} from '@/components/molecules';
import type { ColumnDef } from '@tanstack/react-table';
import type { Sale, AnalyticsSales } from '@/types';

interface SalesTabProps {
  showEmptyChart: boolean;
  showAnalyticsEmptyState: boolean;
  onRowClick: (sale: Sale) => void;
  salesData: Sale[];
  columns: ColumnDef<Sale>[];
  monthlySalesData: Array<{ month: string; sales: number }>;
  emptyChartData: Array<{ month: string; sales: number }>;
  chartConfig: {
    sales: { label: string; color: string };
  };
  analyticsData?: AnalyticsSales;
  topMonth?: string;
  topYear?: string;
  topBuyer?: string;
  topBuyerSubtitle?: string;
  salesCountLabel?: string;
}

export const SalesTab = ({
  showEmptyChart,
  showAnalyticsEmptyState,
  onRowClick,
  salesData,
  columns,
  monthlySalesData,
  emptyChartData,
  chartConfig,
  analyticsData,
  topMonth = '—',
  topYear = new Date().getFullYear().toString(),
  topBuyer = '—',
  topBuyerSubtitle = '—',
  salesCountLabel = '0 sold so far',
}: SalesTabProps) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <AnalyticsChart
            title="Total Sales"
            amount={
              showEmptyChart
                ? '₦0.00'
                : `₦${analyticsData?.totalSales?.toLocaleString() || '0'}`
            }
            showEmptyChart={showEmptyChart}
            chartData={monthlySalesData}
            emptyChartData={emptyChartData}
            chartConfig={chartConfig}
          />
        </div>
        <div className="space-y-4">
          <MetricCard
            title="Top year"
            value={topYear}
            subtitle={salesCountLabel}
            icon="trending"
            showEmpty={showEmptyChart}
          />
          <MetricCard
            title="Top month"
            value={topMonth}
            subtitle={topYear}
            icon="calendar"
            showEmpty={showEmptyChart}
          />
          <MetricCard
            title="Top buyer"
            value={topBuyer}
            subtitle={topBuyerSubtitle}
            icon="user"
            showEmpty={showEmptyChart}
          />
        </div>
      </div>

      <div className="space-y-4">
        <Heading size="lg">Recent orders</Heading>
        {showAnalyticsEmptyState ? (
          <AnalyticsEmptyState message="You don't have any recent orders" />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={salesData}
              onRowClick={onRowClick}
            />
            <div className="flex justify-center mt-4">
              <Button variant="outline" className="text-gray-600">
                View All
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
